/** Modelos do Claude que o chat oferece (o mais capaz primeiro). */
export const CLAUDE_MODELS = [
  { id: "claude-opus-5-5", name: "Claude Opus 5.5", hint: "O mais capaz, para tarefas difíceis" },
  { id: "claude-sonnet-5-5", name: "Claude Sonnet 5.5", hint: "Rápido e muito bom no dia a dia" },
  { id: "claude-haiku-5-5", name: "Claude Haiku 5.5", hint: "O mais rápido e econômico" },
] as const;
export type ClaudeModelId = (typeof CLAUDE_MODELS)[number]["id"];
export const isClaudeModel = (id: unknown): id is ClaudeModelId => CLAUDE_MODELS.some((m) => m.id === id);

/** Nível de esforço (quanto o modelo pensa antes de responder). */
export const AI_EFFORTS = ["low", "medium", "high"] as const;
export type AiEffort = (typeof AI_EFFORTS)[number];
export const AI_EFFORT_LABELS: Record<AiEffort, string> = { low: "Baixo", medium: "Médio", high: "Alto" };
export const isAiEffort = (v: unknown): v is AiEffort => AI_EFFORTS.includes(v as AiEffort);

/**
 * Preço da API da Anthropic em US$ por milhão de tokens (tabela oficial de 2026-10). Escrita no
 * cache de 5 minutos custa 1,25× a entrada. O Haiku 5.5 cobra 5× acima de 100 mil tokens de prompt.
 */
export const CLAUDE_PRICES: Record<
  ClaudeModelId,
  { input: number; output: number; cacheRead: number; cacheWrite: number; longPrompt?: number }
> = {
  "claude-opus-5-5": { input: 4, output: 20, cacheRead: 0.2, cacheWrite: 5 },
  "claude-sonnet-5-5": { input: 2, output: 10, cacheRead: 0.2, cacheWrite: 2.5 },
  "claude-haiku-5-5": { input: 0.1, output: 0.5, cacheRead: 0.01, cacheWrite: 0.125, longPrompt: 100_000 },
};

/** Custo estimado (US$) de uma resposta pelo `usage`; modelo desconhecido: null. */
export function claudeCost(
  model: string,
  usage: { inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheWriteTokens: number },
): number | null {
  const id = CLAUDE_MODELS.find((m) => model === m.id || model.startsWith(`${m.id}-`))?.id;
  if (!id) return null;
  const p = CLAUDE_PRICES[id];
  const prompt = usage.inputTokens + usage.cacheReadTokens + usage.cacheWriteTokens;
  const k = p.longPrompt && prompt > p.longPrompt ? 5 : 1;
  return (
    (usage.inputTokens * p.input * k +
      usage.outputTokens * p.output * k +
      usage.cacheReadTokens * p.cacheRead * k +
      usage.cacheWriteTokens * p.cacheWrite * k) /
    1_000_000
  );
}

/** Nome curto do modelo ("Opus 5.5"); desconhecido: o próprio id. */
export const claudeModelName = (model: string) =>
  CLAUDE_MODELS.find((m) => model === m.id || model.startsWith(`${m.id}-`))?.name.replace("Claude ", "") ??
  model;
